import {
  Stack,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
} from '@mui/material';
import { Card } from '@liminal/web-ui/primitives/Card';
import { useUserLinkedAccounts, useUserRecipients } from '@/hooks/useUsers';
import { formatDate } from '@liminal/utils/date';
import { truncateMiddle } from '@liminal/utils/truncate';
import { StatusPill, type StatusPillTone } from '@liminal/web-ui/primitives/StatusPill';
import { CopyableId } from '@liminal/web-ui/display/CopyableId';
import { DataTableBody } from '@liminal/web-ui/display/DataTableBody';
import { MonoCell } from '@/components/ui/MonoCell';
import { SortableTableCell } from '@liminal/web-ui/display/SortableTableCell';
import { useTableSort } from '@liminal/web-ui/primitives/useTableSort';
import type { LinkedAccount, Recipient } from '@/api/types';

function linkedAccountStatusVariant(status?: string): StatusPillTone {
  if (!status) return 'neutral';
  if (status.includes('ACTIVE')) return 'success';
  if (status.includes('INACTIVE')) return 'error';
  return 'neutral';
}

function linkedAccountStatusLabel(status?: string): string {
  if (!status) return 'Unknown';
  return status.replace('LINKED_ACCOUNT_STATUS_', '').replace(/_/g, ' ');
}

function recipientTypeLabel(type?: string): string {
  if (!type) return 'Unknown';
  return type.replace('RECIPIENT_TYPE_', '').replace(/_/g, ' ');
}

function recipientTypeVariant(type?: string): StatusPillTone {
  if (!type) return 'neutral';
  if (type.includes('BANK')) return 'info';
  if (type.includes('WALLET')) return 'warning';
  if (type.includes('LIMINAL')) return 'success';
  return 'neutral';
}

function recipientSummary(recipient: {
  recipientType?: string;
  bankSummary?: { bankName?: string; mask?: string; currency?: string; country?: string };
  walletSummary?: { walletAddress?: string; chainName?: string; currency?: string };
  liminalUserSummary?: { displayTag?: string; firstName?: string; lastName?: string };
}): string {
  if (recipient.recipientType?.includes('BANK') && recipient.bankSummary) {
    const b = recipient.bankSummary;
    return [b.bankName, b.mask ? `****${b.mask}` : null, b.currency, b.country]
      .filter(Boolean)
      .join(' • ');
  }
  if (recipient.recipientType?.includes('WALLET') && recipient.walletSummary) {
    const w = recipient.walletSummary;
    return [w.chainName, w.currency, w.walletAddress ? truncateMiddle(w.walletAddress) : null]
      .filter(Boolean)
      .join(' • ');
  }
  if (recipient.recipientType?.includes('LIMINAL') && recipient.liminalUserSummary) {
    const l = recipient.liminalUserSummary;
    return [l.firstName, l.lastName, l.displayTag ? `@${l.displayTag}` : null]
      .filter(Boolean)
      .join(' ');
  }
  return '—';
}

/** One user's whole set arrives per response, so both of these are true sorts. */
const ACCOUNT_SORT_ACCESSORS = {
  bankName: (acct: LinkedAccount) => acct.bankName || acct.displayName,
  mask: (acct: LinkedAccount) => acct.mask,
  currency: (acct: LinkedAccount) => acct.currency,
  country: (acct: LinkedAccount) => acct.country,
  paymentMethod: (acct: LinkedAccount) => acct.paymentMethod,
  status: (acct: LinkedAccount) => linkedAccountStatusLabel(acct.status),
  createdAt: (acct: LinkedAccount) => acct.createdAt,
};

const RECIPIENT_SORT_ACCESSORS = {
  // The cell shows the full name, falling back to the nickname — sorting has
  // to read the same way round or the column orders by something invisible.
  name: (r: Recipient) => [r.firstName, r.lastName].filter(Boolean).join(' ') || r.nickname,
  recipientType: (r: Recipient) => recipientTypeLabel(r.recipientType),
  details: (r: Recipient) => recipientSummary(r),
  email: (r: Recipient) => r.email,
  createdAt: (r: Recipient) => r.createdAt,
};

// One array per table drives its header cells and the DataTableBody column count.
const ACCOUNT_COLUMNS = [
  { column: 'bankName', label: 'Bank' },
  { column: 'mask', label: 'Account' },
  { column: 'currency', label: 'Currency' },
  { column: 'country', label: 'Country' },
  { column: 'paymentMethod', label: 'Payment Method' },
  { column: 'status', label: 'Status' },
  { column: 'createdAt', label: 'Created' },
] as const;

const RECIPIENT_COLUMNS = [
  { column: 'name', label: 'Name' },
  { column: 'recipientType', label: 'Type' },
  { column: 'details', label: 'Details' },
  { column: 'email', label: 'Email' },
  { column: 'createdAt', label: 'Created' },
] as const;

export function LinkedAccountsRecipientsSection({ userId }: { userId: string }) {
  const {
    data: accountsData,
    isLoading: accountsLoading,
    isError: accountsError,
  } = useUserLinkedAccounts(userId);
  const {
    data: recipientsData,
    isLoading: recipientsLoading,
    isError: recipientsError,
  } = useUserRecipients(userId);

  const { rows: accounts, sort: accountSort } = useTableSort(
    accountsData?.accounts ?? [],
    ACCOUNT_SORT_ACCESSORS,
    'createdAt',
    'desc',
  );
  const { rows: recipients, sort: recipientSort } = useTableSort(
    recipientsData?.recipients ?? [],
    RECIPIENT_SORT_ACCESSORS,
    'createdAt',
    'desc',
  );

  // The two tables load and fail independently: a slow recipients lookup no
  // longer holds the bank accounts back, and a failed one says so instead of
  // reading as "no recipients".
  return (
    <Stack spacing={3}>
      {/* Linked Bank Accounts */}
      <Card variant="elevated" padding="none">
          <Typography variant="h6" sx={{ px: 2, pt: 2, pb: 1 }}>
            Linked Bank Accounts
          </Typography>
          <Table>
            <TableHead>
              <TableRow>
                {ACCOUNT_COLUMNS.map(({ column, label }) => (
                  <SortableTableCell key={column} column={column} sort={accountSort}>
                    {label}
                  </SortableTableCell>
                ))}
              </TableRow>
            </TableHead>
            <DataTableBody
              columns={ACCOUNT_COLUMNS.length}
              isLoading={accountsLoading}
              isError={accountsError}
              isEmpty={accounts.length === 0}
              emptyMessage="No linked bank accounts."
              errorMessage="Could not load this user's linked accounts."
            >
              {accounts.map((acct, i) => (
                <TableRow key={acct.id ?? i}>
                  <TableCell>{acct.bankName || acct.displayName || '—'}</TableCell>
                  <MonoCell>{acct.mask ? `****${acct.mask}` : '—'}</MonoCell>
                  <TableCell>{acct.currency || '—'}</TableCell>
                  <TableCell>{acct.country || '—'}</TableCell>
                  <TableCell>{acct.paymentMethod?.replace(/_/g, ' ') || '—'}</TableCell>
                  <TableCell>
                    <StatusPill
                      label={linkedAccountStatusLabel(acct.status)}
                      tone={linkedAccountStatusVariant(acct.status)}
                      size="sm"
                    />
                  </TableCell>
                  <TableCell>
                    {acct.createdAt ? formatDate(acct.createdAt, { month: 'short' }) : '—'}
                  </TableCell>
                </TableRow>
              ))}
            </DataTableBody>
          </Table>
      </Card>

      {/* Recipients */}
      <Card variant="elevated" padding="none">
          <Typography variant="h6" sx={{ px: 2, pt: 2, pb: 1 }}>
            Recipients
            {recipientsData?.total != null && (
              <Typography component="span" variant="body2" color="text.disabled" sx={{ ml: 1 }}>
                ({recipientsData.total})
              </Typography>
            )}
          </Typography>
          <Table>
            <TableHead>
              <TableRow>
                {RECIPIENT_COLUMNS.map(({ column, label }) => (
                  <SortableTableCell key={column} column={column} sort={recipientSort}>
                    {label}
                  </SortableTableCell>
                ))}
              </TableRow>
            </TableHead>
            <DataTableBody
              columns={RECIPIENT_COLUMNS.length}
              isLoading={recipientsLoading}
              isError={recipientsError}
              isEmpty={recipients.length === 0}
              emptyMessage="No recipients found."
              errorMessage="Could not load this user's recipients."
            >
              {recipients.map((r, i) => {
                const walletAddress = r.recipientType?.includes('WALLET')
                  ? r.walletSummary?.walletAddress
                  : undefined;
                return (
                  <TableRow key={r.id ?? i}>
                    <TableCell>
                      {[r.firstName, r.lastName].filter(Boolean).join(' ') || r.nickname || '—'}
                      {r.nickname && r.firstName && (
                        <Typography variant="caption" color="text.disabled" sx={{ ml: 0.5 }}>
                          ({r.nickname})
                        </Typography>
                      )}
                      {r.businessName && (
                        <Typography variant="caption" color="text.disabled" display="block">
                          {r.businessName}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <StatusPill
                        label={recipientTypeLabel(r.recipientType)}
                        tone={recipientTypeVariant(r.recipientType)}
                        size="sm"
                      />
                    </TableCell>
                    <TableCell>
                      {walletAddress ? (
                        <CopyableId value={walletAddress} truncate label="Copy wallet address" />
                      ) : (
                        <Typography variant="body2">{recipientSummary(r)}</Typography>
                      )}
                    </TableCell>
                    <TableCell>{r.email || '—'}</TableCell>
                    <TableCell>
                      {r.createdAt ? formatDate(r.createdAt, { month: 'short' }) : '—'}
                    </TableCell>
                  </TableRow>
                );
              })}
            </DataTableBody>
          </Table>
      </Card>
    </Stack>
  );
}
